import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import { selectCurrentagency } from "../../redux/features/authSlice";
import { useGetAgencyDashboardQuery } from "../../redux/api/authApi";
import CustomizeDashboardModal from "../../components/dashboard/CustomizeBtn";
import RenderGroup from "./RenderGroup";

export default function Dashboard() {
  const [data, setData] = useState({});
  const [openCustomization, setOpenCustomization] = useState(false);
  const currentAgency = useSelector(selectCurrentagency);

  const {
    data: dashboard,
    isFetching,
    isSuccess,
  } = useGetAgencyDashboardQuery(currentAgency?.agencyid, {
    refetchOnMountOrArgChange: true,
  });

  // Default widget settings
  const defaultSettings = {
  order: [
    "AccountsCount",
    "AccountsCharts",
    "showLeads",
    "showTasks",
    "showPerformance",
    "showPropertiesByCity",
    "showGraphs",
  ],
  visibility: {
    AccountsCount: true,
    AccountsCharts: true,
    showLeads: true,
    showTasks: true,
    showPerformance: true,
    showPropertiesByCity: true,
    showGraphs: true,
  },
};

const [customizationSettings, setCustomizationSettings] = useState(defaultSettings);

// Load from localStorage
useEffect(() => {
  const saved = localStorage.getItem("dashboardCustomization");

  if (saved) {
    try {
      const parsed = JSON.parse(saved);

      // ✅ Check if it’s missing keys or less than 6 order items
      const isInvalid =
        !parsed ||
        !Array.isArray(parsed.order) ||
        parsed.order.length < Object.keys(defaultSettings.visibility).length ||
        Object.keys(parsed.visibility || {}).length <
          Object.keys(defaultSettings.visibility).length;

      if (isInvalid) {
        // Reset to defaults
        setCustomizationSettings(defaultSettings);
        localStorage.setItem(
          "dashboardCustomization",
          JSON.stringify(defaultSettings)
        );
      } else {
        setCustomizationSettings(parsed);
      }
    } catch {
      setCustomizationSettings(defaultSettings);
      localStorage.setItem(
        "dashboardCustomization",
        JSON.stringify(defaultSettings)
      );
    }
  } else {
    // If nothing saved at all
    localStorage.setItem(
      "dashboardCustomization",
      JSON.stringify(defaultSettings)
    );
  }
}, []);

  // Update data when fetched
  useEffect(() => {
    if (isSuccess && dashboard) {
      setData(dashboard);
    }
  }, [isSuccess, dashboard]);

  // Save settings to localStorage
  const saveSettings = (newSettings) => {
    setCustomizationSettings(newSettings);
    localStorage.setItem("dashboardCustomization", JSON.stringify(newSettings));
  };

  const handleToggle = (key) => {
    const newSettings = {
      ...customizationSettings,
      visibility: {
        ...customizationSettings.visibility,
        [key]: !customizationSettings.visibility[key],
      },
    };
    saveSettings(newSettings);
  };

  const handleReorder = (newOrder) => {
    const newSettings = { ...customizationSettings, order: newOrder };
    saveSettings(newSettings);
  };

  if (isFetching) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="50vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box mt={4} mx={{xs: 1, sm: 4}} position="relative">
        <Box
          position="absolute"
          sx={{
            top: { xs: -50, md: 10 },
            right: { xs: 0, md: 16 },
            zIndex: 10,
            // Mirror the button to the opposite side when the page is RTL
            // (e.g. Arabic / Urdu via Google Translate).
            '[dir="rtl"] &': {
              right: "auto",
              left: { xs: 0, md: 16 },
            },
          }}
        >
          <Button variant="outlined" onClick={() => setOpenCustomization(true)}>
            Customize
          </Button>
        </Box>

        <CustomizeDashboardModal
          open={openCustomization}
          onClose={() => setOpenCustomization(false)}
          customizationSettings={customizationSettings}
          handleToggle={handleToggle}
          handleReorder={handleReorder}
        />

        <Typography variant="h2" fontWeight="bold" mb={1}>
          Welcome,{" "}
          <span className="text-green">{currentAgency?.agencyname}</span>
        </Typography>
        <Typography variant="p">
          Get a quick overview of your leads and customer data
        </Typography>
      </Box>

      {/* Render groups dynamically */}
      {customizationSettings?.order?.map(
        (key) =>
          customizationSettings.visibility[key] && (
            <Box key={key} mx={{ xs: -2, sm: 0 }} mb={3}>
            <RenderGroup
              id={key}
              data={data}
              isFetching={isFetching}
              isSuccess={isSuccess}
              currentAgency={currentAgency}
            />
            </Box>
          )
      )}

      {/* Empty dashboard fallback */}
      {!Object.values(customizationSettings.visibility || {}).some(Boolean) && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="50vh"
          textAlign="center"
        >
          <Typography
            variant="h5"
            fontWeight="600"
            color="text.secondary"
            mb={1}
          >
            Your dashboard is currently empty
          </Typography>
          <Typography variant="body1" color="text.secondary" mb={3}>
            All dashboard sections are turned off. Use the{" "}
            <strong>“Customize”</strong> button above to enable or reorder
            widgets.
          </Typography>
          <Button
            variant="contained"
            sx={{ color: "white" }}
            onClick={() => setOpenCustomization(true)}
          >
            Open Customize Panel
          </Button>
        </Box>
      )}
    </>
  );
}
